/**
 * 广告管理页的 URL 状态（R20/R22 + 用户 2026-10-04 原话第 2 条）。
 *
 * 沿用 `urlState.ts` 的三条原则：URL 是筛选状态的唯一真相；默认值不写入 URL；
 * 解析对垃圾输入免疫（用户会手改 URL）。
 *
 * ## 2026-10-04 的两处变更
 *
 *  1. **时间范围快捷选项**：新增 `range`（`today|yesterday|last7|last30|last90|last180|
 *     this_month|month:YYYY-MM|custom`）。非 `custom` 时 `from`/`to` 由 preset **算出来**
 *     （不是从 URL 直接读），这样「按钮高亮」与「实际区间」永远一致；`from`/`to` 仍然写进
 *     URL，保证链接自描述、可分享。区间算法在 `dateRange.ts`（有 30+ 条边界测试）。
 *  2. **站点筛选移到右上角全局栏**（R12；`store/contextStore.ts` 用 localStorage 持久化）。
 *     本页 `search` 不再有 `shopId`：两处各存一份会静默打架（"右上角选了 US、
 *     页面显示全部站点"）。旧链接里的 `shopId` 会被忽略并丢弃。
 *
 * ## 2026-10-05 第十五批
 *
 *  · 「最近 N 天」改为**不含今天**（`[今天-N, 今天-1]`，用户第 2 条）；
 *  · `from`/`to`/`range` 的解析与序列化抽到**共享模块** `rangeSearch.ts`
 *    （与经营概览页同一份，避免"同一屏两个区间"），本文件只保留本页独有的筛选字段。
 *
 * ## 与 `AdsSearch` 的差别
 *
 * 这里的字段用**独立 key**，`router.tsx` 的 `stringifySearch` 按 key 集合区分两套
 * schema（互不重叠），因此不会互相污染。
 */

import type { AdTreeScaleBasis, AdTreeSortBasis, AdTreeSortDirection } from "@ams/contracts";
import { DEFAULT_DATE_RANGE_KEY } from "./dateRange.js";
import {
  defaultRangeSearch,
  parseRangeSearch,
  rangeSearchToParams,
  RANGE_SEARCH_KEYS,
  type RangeSearch,
} from "./rangeSearch.js";

/**
 * ⭐ 2026-10-30（用户裁定「广告管理增加国家的筛选，默认全部，可单选或多选」）：
 * 把输入（URL 参数 / 多选面板）归一化成 ISO-2 国家码数组。
 *
 * 规则（**宽容解析、严格输出**）：
 *  · 分隔符：英文/中文逗号（用户会从别处粘清单）；
 *  · 每项 trim + upper-case；只保留 `^[A-Z]{2}$`；
 *  · 去重保序；**非法 token 静默丢弃**（URL 是用户可手改的输入 —— 与 `styleCodes` 同一约定；
 *    后端另有严格校验：直接打 API 传非法码会 400 ✓ 两层都不含糊）。
 */
export function parseCountryCodes(raw: unknown): readonly string[] {
  if (typeof raw !== "string" || raw.trim() === "") return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const piece of raw.split(/[,，]/)) {
    const code = piece.trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(code)) continue;
    if (seen.has(code)) continue;
    seen.add(code);
    out.push(code);
  }
  return out;
}

/** 国家码数组 → URL 值（逗号拼接；空数组 ⇒ `""` = 不筛 = 全部国家）。 */
export function countryCodesToSearch(codes: readonly string[]): string {
  return parseCountryCodes(codes.join(",")).join(",");
}

export interface AdManagementSearch extends RangeSearch {
  /**
   * ⭐ 2026-10-30 国家筛选：**逗号分隔的 ISO-2 国家码**（已归一化：大写、去重、去非法）。
   *
   * 语义 = 事实表按 `country=XX` 切片（与 `TOTAL` **互斥**）；`""` = **全部国家**（默认）。
   * 进 URL（可分享 / 刷新还原）；改动即重取。
   * ⛔ 不做任何口径提示（用户明确否决：加了国家维度也是同一份 Meta 自归因口径）。
   */
  readonly countries: string;
  /** 语言筛选（R24）；`""` = 全部语言。 */
  readonly language: string;
  /**
   * **货号筛选（2026-10-04 第八批）**：逗号分隔的货号白名单（已归一化：大写、去重、去空）。
   *
   * 语义 = 只显示「下属 `effective_status='ACTIVE'` 广告名解析出的货号合集里包含任一
   * 该货号」的系列（与货号列同源解析）。`""` = 不筛。
   * 进 URL（可分享 / 刷新还原）；垃圾 token 在解析时被丢弃（URL 是用户可手改的输入）。
   */
  readonly styleCodes: string;
  /**
   * **货号分组筛选（2026-10-05 第十八批）**：逗号分隔的**分组 id** 白名单。
   *
   * 语义 = 只显示「下属 ACTIVE 广告名解析出的货号**命中任一选中组里的货号**」的实体；
   * 与上面的 `styleCodes` **同构但独立**（两个都给时后端取**交集** = 同时满足）。
   * 进 URL（可分享 / 刷新还原）；垃圾 id 在解析时被丢弃（URL 是用户可手改的输入）。
   * `""` = 不筛。分组按店铺，切换店铺后旧 id 自然落空。
   */
  readonly styleCodeGroups: string;
  /**
   * **素材筛选（2026-10-04 第十二批 · 用户第 10 条；第八批的占位字段名沿用）**：
   * 逗号分隔的**关键词**白名单（已归一化：trim、去空、大小写不敏感去重、保序）。
   *
   * 用户口径原文：「素材筛选 按 **素材名字**，例如 `V-RS171-TK渠道号-多色静物-1`」。
   * 语义 = 只显示「**存在某条 `effective_status='ACTIVE'` 广告的名字包含任一关键词**」
   * 的实体；**大小写不敏感的纯子串匹配**（不做分词）。`""` = 不筛。
   * 进 URL（可分享 / 刷新还原）；关键词里的空格 / `-` / 中文都**原样保留**（素材名就是这样）。
   */
  readonly material: string;
  /** 三分组基准，默认日预算。 */
  readonly scaleBasis: AdTreeScaleBasis;
  /**
   * **列表排序基准**，默认 `spend`（用户 2026-10-04 第四批口径 A：默认花费倒序）。
   * 与 `scaleBasis`（分组）**刻意分开**——R22「三分组默认按日预算」不变。
   * 进 URL，可分享。
   */
  readonly sort: AdTreeSortBasis;
  /**
   * **列表排序方向**（2026-10-04 第七批）：`desc`（默认，= 改造前的"花费倒序"）
   * 或 `asc`。与 `sort`（排序键）配套，由**列头点击**与筛选条切换共同维护，
   * 二者共享这一份 URL 状态（不允许两处各存一份）。
   */
  readonly sortDir: AdTreeSortDirection;
  /**
   * `true` = **显示全部（含暂停）**（后端 `activeOnly=false`）；默认 false = 只显示生效中的项。
   *
   * ⚠️ 2026-10-06（task-9，用户口径「投放中就显示」）：本开关**不再**隐式决定
   * `deliveringOnly` ——「有没有花费」拆到下面那个独立窄开关。老链接 `allAds=1` 的语义
   * （含暂停 + 不过滤花费）保持不变。
   */
  readonly allAds: boolean;
  /**
   * **独立窄开关（2026-10-06 · task-9）**：`true` = 只显示**选定区间内确实投放过**
   * （该层级事实里 `spend_cents > 0`）的项（= 旧默认行为）。
   *
   * 默认 **`false`**（用户口径：「我理解是投放中，就显示」——生效中就显示，不看花费）。
   * 与 `allAds` **互相独立**：可以「含暂停 + 只看有花费」，也可以「只看生效 + 不看花费」。
   * 进 URL（仅 `true` 时写出 `deliveringOnly=true`）。
   */
  readonly deliveringOnly: boolean;
  /**
   * **「暂停-建议看」筛选（2026-10-06 · task-3）**：`true` = 只显示「子树里存在 ≥1 条
   * 「值得看（`worth_watching` 物化标记）且当前暂停」广告」的**系列**。
   *
   * 默认 `false`；进 URL（仅 `true` 时写 `pausedWorthWatching=true`，可刷新/分享还原）。
   * 页面把它映射成接口参数 `pausedWorthWatchingOnly=true`。
   * 只筛顶层系列：展开系列后**不隐藏**其他广告（后端子级查询会忽略该参数）。
   */
  readonly pausedWorthWatching: boolean;
  /**
   * **「包含已结束(N)」开关（2026-10-06 · task-14）**：`true` = 把「排期已结束」的
   * 历史系列也显示出来（后端 `includeEnded=true`）。
   *
   * 默认 **`false`** = 隐藏历史噪音（AU 站 442 条「生效·未投放」里 431 条是排期已结束，
   * 会把唯一那条真实信号淹没）。进 URL（仅 `true` 时写 `includeEnded=true`）。
   *
   * ⚠️ 过滤在**服务端 SQL**：只作用于顶层系列集合（分页 / 计数正确），
   * 展开系列后的子级**不受影响**（仍能看到该系列里真正的广告）。
   */
  readonly includeEnded: boolean;
  /**
   * **整店历史表是否展开**（2026-10-04 第九批 · 交付物 A）。
   *
   * 用户要求「这个窗口**可以弹开，也可以折叠**」，并允许折叠状态进 URL 或 localStorage
   * **二者择一**。这里选 **URL**：与页面其它筛选/开关同一套 `urlState` 纪律
   * （可分享、刷新还原、无隐藏状态），且不需要再引入一份持久化。
   * 默认折叠（`false`）→ **不写入 URL**（默认值不落 URL 的既有规则）。
   */
  readonly history: boolean;
}

/** 本页 search 的 key 集合（`stringifySearch` 用它识别 schema）。 */
export const AD_MANAGEMENT_SEARCH_KEYS: readonly string[] = [
  ...RANGE_SEARCH_KEYS,
  "language",
  "styleCodes",
  "styleCodeGroups",
  "material",
  "scaleBasis",
  "sort",
  "sortDir",
  "allAds",
  "deliveringOnly",
  "pausedWorthWatching",
  "includeEnded",
  "history",
];

const LANGUAGE = /^[a-z]{2}(-[a-z]{2})?$/;

export const AD_MANAGEMENT_DEFAULT_BASIS: AdTreeScaleBasis = "daily_budget";
/** 列表默认排序 = 花费降序（用户口径 A；分组基准仍是 `daily_budget`）。 */
export const AD_MANAGEMENT_DEFAULT_SORT: AdTreeSortBasis = "spend";
/** 列表默认排序方向 = 降序（2026-10-04 第七批；缺省时与改造前逐字一致）。 */
export const AD_MANAGEMENT_DEFAULT_SORT_DIRECTION: AdTreeSortDirection = "desc";
/**
 * 默认展示窗口长度（天，**含首尾**）——与默认快捷选项「最近 7 天」同义。
 *
 * ⚠️ 2026-10-05 起「最近 N 天」**不含今天**：7 天 = `[今天-7, 今天-1]`（见 `dateRange.ts`）。
 */
export const AD_MANAGEMENT_DEFAULT_WINDOW_DAYS = 7;

/** 货号筛选的**上限**（与 `apps/api/src/params.ts` 的 `MAX_STYLE_CODE_FILTERS` 一致）。 */
export const MAX_STYLE_CODE_FILTERS = 64;

/**
 * 素材关键词的**上限**（与 `apps/api/src/params.ts` 的 `MAX_MATERIAL_FILTERS` /
 * `MAX_MATERIAL_KEYWORD_LENGTH` 一致）。
 */
export const MAX_MATERIAL_FILTERS = 32;
export const MAX_MATERIAL_KEYWORD_LENGTH = 64;

/**
 * 素材筛选输入 → **规范串**（trim / 去空 / 大小写不敏感去重 / 保序 / 逗号拼接）。
 *
 * 与货号筛选的差别（都是用户明确的不同口径）：
 *  · **不做大写归一化**（素材名大小写有意义，匹配本身大小写不敏感）；
 *  · **允许空格 / `-` / 中文 / 任意可见字符**——只按**半角逗号**分隔，不做字符集校验；
 *  · 超长 / 超个数的项按上限截断，绝不把垃圾送进后端触发 400。
 * 与后端解析（`parseOptionalMaterialKeywords`）同一套规则，保证 URL / 请求 / 提示三者一致。
 */
export function normalizeMaterialFilter(raw: string): string {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const piece of raw.split(",")) {
    const keyword = piece.trim().slice(0, MAX_MATERIAL_KEYWORD_LENGTH);
    if (keyword === "") continue;
    const key = keyword.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(keyword);
    if (out.length >= MAX_MATERIAL_FILTERS) break;
  }
  return out.join(",");
}

/** 规范串 → 素材关键词数组（`""` → `[]`，即"没有该筛选"）。 */
export function materialListFromSearch(
  search: Pick<AdManagementSearch, "material">,
): readonly string[] {
  return search.material === "" ? [] : search.material.split(",");
}

/**
 * 货号筛选输入 → **规范串**（大写 / 去重 / 保序 / 逗号拼接）。
 *
 * 为什么在 URL 里存规范串而不是用户原始输入：URL 是筛选状态的唯一真相，
 * 规范化只做一次，`已筛选` 提示、请求参数、分享链接三者必然一致。
 * 非法字符（空格、`-`、中文…）按**分隔符**处理并丢弃空段：这样
 * 「R625, R626」「r625 r626」「R625/R626」都能用，而不会把垃圾送进后端触发 400。
 */
export function normalizeStyleCodeFilter(raw: string): string {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const piece of raw.split(/[^A-Za-z0-9]+/)) {
    if (piece === "") continue;
    const code = piece.toUpperCase();
    if (seen.has(code)) continue;
    seen.add(code);
    out.push(code);
    if (out.length >= MAX_STYLE_CODE_FILTERS) break;
  }
  return out.join(",");
}

/** 规范串 → 货号数组（`""` → `[]`，即"没有该筛选"）。 */
export function styleCodeListFromSearch(
  search: Pick<AdManagementSearch, "styleCodes">,
): readonly string[] {
  return search.styleCodes === "" ? [] : search.styleCodes.split(",");
}

/**
 * 分组 id 上限（与 `apps/api/src/params.ts` 的 `MAX_STYLE_CODE_GROUP_FILTERS` 一致）。
 * 超出的尾部丢弃（URL 是用户可手改的输入；不把垃圾送进后端触发 400）。
 */
export const MAX_STYLE_CODE_GROUP_FILTERS = 32;

/**
 * 货号分组筛选输入 → **规范串**（trim / 去空 / 去重 / 保序 / 逗号拼接）。
 *
 * 分组 id 由服务端生成（`scg_<uuid>`），因此只按**逗号**分隔、只保留
 * `[A-Za-z0-9_-]` 形状的段；非法段丢弃。大小写**保持不变**（id 大小写敏感）。
 */
export function normalizeStyleCodeGroupFilter(raw: string): string {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const piece of raw.split(",")) {
    const id = piece.trim();
    if (id === "" || id.length > 64 || !/^[A-Za-z0-9_-]+$/.test(id)) continue;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(id);
    if (out.length >= MAX_STYLE_CODE_GROUP_FILTERS) break;
  }
  return out.join(",");
}

/** 规范串 → 分组 id 数组（`""` → `[]`，即"没有该筛选"）。 */
export function styleCodeGroupListFromSearch(
  search: Pick<AdManagementSearch, "styleCodeGroups">,
): readonly string[] {
  return search.styleCodeGroups === "" ? [] : search.styleCodeGroups.split(",");
}

/** 切换一个分组的选中态（多选；已在 → 移除）。 */
export function toggleStyleCodeGroupFilter(current: string, groupId: string): string {
  const list = styleCodeGroupListFromSearch({ styleCodeGroups: current });
  const next = list.includes(groupId) ? list.filter((id) => id !== groupId) : [...list, groupId];
  return normalizeStyleCodeGroupFilter(next.join(","));
}

/**
 * ⚠️ 2026-10-04 第十二批：`normalizeReservedMaterial`（占位期的"只 trim + 限长"）
 * **已被 `normalizeMaterialFilter` 取代**——素材筛选现在是真筛选。这里保留一个
 * **废弃别名**只为兼容可能仍引用它的调用方（等价于 `normalizeMaterialFilter`），
 * 新代码一律用 `normalizeMaterialFilter`。
 *
 * @deprecated 用 `normalizeMaterialFilter`。
 */
export function normalizeReservedMaterial(raw: string): string {
  return normalizeMaterialFilter(raw);
}

function readString(raw: Record<string, unknown>, key: string): string | null {
  const value = raw[key];
  return typeof value === "string" ? value : null;
}

/**
 * 默认区间 = 最近 7 天。日期用**本地日**（页面口径与店铺业务日一致）；
 * 区间口径（**不含今天**）= `[今天-7, 今天-1]`，唯一来源 = `dateRange.ts`。
 */
export function defaultAdManagementSearch(now: Date = new Date()): AdManagementSearch {
  const { from, to, range } = defaultRangeSearch(DEFAULT_DATE_RANGE_KEY, now);
  return {
    from,
    to,
    range,
    language: "",
    countries: "",
    styleCodes: "",
    styleCodeGroups: "",
    material: "",
    scaleBasis: AD_MANAGEMENT_DEFAULT_BASIS,
    sort: AD_MANAGEMENT_DEFAULT_SORT,
    sortDir: AD_MANAGEMENT_DEFAULT_SORT_DIRECTION,
    allAds: false,
    // 2026-10-06（task-9）：默认「投放中就显示」——不要求区间内有花费。
    deliveringOnly: false,
    // 2026-10-06（task-3）：「暂停-建议看」筛选默认关闭。
    pausedWorthWatching: false,
    // 2026-10-06（task-14）：「包含已结束」默认关闭（隐藏历史噪音）。
    includeEnded: false,
    history: false,
  };
}

export function parseAdManagementSearch(
  raw: Record<string, unknown>,
  now: Date = new Date(),
): AdManagementSearch {
  // `from`/`to`/`range` 的解析**复用共享实现**（`rangeSearch.ts`，与概览页同一份），
  // 本函数只补本页独有的筛选字段。
  const { from, to, range, includeUnsettled } = parseRangeSearch(raw, DEFAULT_DATE_RANGE_KEY, now);
  const languageRaw = readString(raw, "language");
  // 货号筛选：URL 里的值先规范化（垃圾 token 丢弃，不 400——URL 是用户可手改的输入）。
  const styleCodes = normalizeStyleCodeFilter(readString(raw, "styleCodes") ?? "");
  // 货号分组筛选（第十八批）：只保留形状合法的分组 id（垃圾段丢弃，不 400）。
  const styleCodeGroups = normalizeStyleCodeGroupFilter(readString(raw, "styleCodeGroups") ?? "");
  // 素材：占位，只原样还原（不进查询）。
  const material = normalizeMaterialFilter(readString(raw, "material") ?? "");
  const basisRaw = readString(raw, "scaleBasis");
  const sortRaw = readString(raw, "sort");
  const sortDirRaw = readString(raw, "sortDir");
  const allAdsRaw = raw["allAds"];
  const deliveringOnlyRaw = raw["deliveringOnly"];
  const pausedWorthWatchingRaw = raw["pausedWorthWatching"];
  const includeEndedRaw = raw["includeEnded"];
  const historyRaw = raw["history"];

  return {
    from,
    to,
    range,
    // 任务 4：「含未结算」开关（默认 false → 不写进 search 对象 / URL）。
    ...(includeUnsettled === true ? { includeUnsettled: true } : {}),
    language: languageRaw !== null && LANGUAGE.test(languageRaw) ? languageRaw : "",
    // ⭐ 2026-10-30 国家筛选：归一化成逗号串（非法 token 丢弃 ⇒ 与 `styleCodes` 同一约定）
    countries: countryCodesToSearch(parseCountryCodes(raw["countries"])),
    styleCodes,
    styleCodeGroups,
    material,
    scaleBasis: basisRaw === "spend" ? "spend" : AD_MANAGEMENT_DEFAULT_BASIS,
    // 垃圾值按默认处理（URL 是用户可手改的输入）：只有明确 `daily_budget` 才切换排序。
    sort: sortRaw === "daily_budget" ? "daily_budget" : AD_MANAGEMENT_DEFAULT_SORT,
    // 垃圾值按默认（降序）处理：URL 是用户可手改的输入，只有明确 `asc` 才切升序。
    sortDir: sortDirRaw === "asc" ? "asc" : AD_MANAGEMENT_DEFAULT_SORT_DIRECTION,
    allAds: allAdsRaw === true || allAdsRaw === "true" || allAdsRaw === "1",
    // 窄开关（task-9）：只有明确真值才打开；缺省 = 不看花费（新默认「投放中就显示」）。
    deliveringOnly: deliveringOnlyRaw === true || deliveringOnlyRaw === "true" || deliveringOnlyRaw === "1",
    // task-3：「暂停-建议看」筛选（只有明确真值才打开；缺省关闭）。
    pausedWorthWatching:
      pausedWorthWatchingRaw === true || pausedWorthWatchingRaw === "true" || pausedWorthWatchingRaw === "1",
    // task-14：「包含已结束」（只有明确真值才打开；缺省关闭 = 隐藏历史噪音）。
    includeEnded: includeEndedRaw === true || includeEndedRaw === "true" || includeEndedRaw === "1",
    // 整店历史表展开态（默认折叠；只有明确真值才展开）。
    history: historyRaw === true || historyRaw === "true" || historyRaw === "1",
  };
}

/**
 * 页面开关 → 接口参数（用户 2026-10-04 第二轮 A；2026-10-06 task-9 **拆成两个独立开关**）。
 *
 *  · `activeOnly`（默认 true）= 只显示生效中的项；打开「显示全部（含暂停）」→ false；
 *  · `deliveringOnly`（**默认 false** · task-9）= 只显示选定区间内确实投放过
 *    （该层级事实里 `spend_cents > 0`）的实体；打开「只显示有花费的」→ true。
 *
 * 两者**互相独立**，不再由同一个 `allAds` 派生。老链接兼容：`allAds=1` 仍只关 `activeOnly`，
 * `deliveringOnly` 取新默认 `false` ⇒「显示全部」链接看到的行**只多不少**。
 *
 * 抽成函数是为了让它可被断言：开关与参数的映射反了，用户会看到与预期**相反**的过滤，
 * 而这种错误在页面上看起来"只是个默认值"。
 */
export function activeOnlyFromSearch(search: Pick<AdManagementSearch, "allAds">): boolean {
  return !search.allAds;
}

/**
 * `deliveringOnly`：**默认 false**（2026-10-06 · task-9）。
 *
 * 用户原话：「我们现在广告的显示模式，不是投放中就显示吗？还是要有花费才显示？
 * **我理解是投放中，就显示的**。」⇒ 默认只按「投放中（生效中）」筛，不要求区间内有花费；
 * 想要旧行为（只看有花费）时打开这个独立窄开关即可。
 *
 * ⚠️ 后端谓词另有一条**审核中豁免**（task-7）：即便本开关为 true，刚创建 / 刚并入、
 * 当刻没有花费事实但 `effective_status` 处于 Meta 审核中的实体仍然可见。
 */
export function deliveringOnlyFromSearch(search: Pick<AdManagementSearch, "deliveringOnly">): boolean {
  return search.deliveringOnly;
}

/**
 * `暂停-建议看` 筛选 → 接口参数（2026-10-06 · task-3）。
 *
 * 页面把它映射成 `pausedWorthWatchingOnly=true`（只筛**系列**；后端在子级查询里忽略它，
 * 因此展开系列后其他广告照常显示）。`N` 由服务端在
 * `includePausedWorthWatchingCount=true` 时随列表响应给出（文案 `暂停-建议看(N)`）。
 */
export function pausedWorthWatchingFromSearch(
  search: Pick<AdManagementSearch, "pausedWorthWatching">,
): boolean {
  return search.pausedWorthWatching;
}

/**
 * `包含已结束` 开关 → 接口参数 `includeEnded`（2026-10-06 · task-14）。
 *
 * 默认 `false` = **隐藏**排期已结束的历史系列（SQL 层过滤，只作用于顶层系列集合）；
 * 打开 = `includeEnded=true`（把历史也显示出来）。`N` 由服务端随列表返回
 * （`notDeliveringSummary.ended`），**与开关状态无关**。
 */
export function includeEndedFromSearch(search: Pick<AdManagementSearch, "includeEnded">): boolean {
  return search.includeEnded;
}

/** 序列化：等于默认值（或空）的字段省略，`from`/`to` 总是显式写出。 */
export function adManagementSearchToParams(search: AdManagementSearch): Record<string, string> {
  // `from`/`to`/`range` 走**共享实现**（与概览页同一份规则），本页只加自己的筛选字段。
  const params: Record<string, string> = rangeSearchToParams(search);
  if (search.language !== "") params["language"] = search.language;
  // ⭐ 2026-10-30 国家筛选：默认（全部国家）不写 URL；选了才写 ⇒ 可分享/刷新还原。
  if (search.countries !== "") params["countries"] = search.countries;
  if (search.styleCodes !== "") params["styleCodes"] = search.styleCodes;
  // 货号分组筛选（第十八批）：选中的分组 id 写回 URL（刷新 / 分享还原）。
  if (search.styleCodeGroups !== "") params["styleCodeGroups"] = search.styleCodeGroups;
  // 素材筛选（第十二批起是真筛选）：规范化后的关键词串写回 URL（刷新 / 分享还原）。
  if (search.material !== "") params["material"] = search.material;
  if (search.scaleBasis !== AD_MANAGEMENT_DEFAULT_BASIS) params["scaleBasis"] = search.scaleBasis;
  if (search.sort !== AD_MANAGEMENT_DEFAULT_SORT) params["sort"] = search.sort;
  if (search.sortDir !== AD_MANAGEMENT_DEFAULT_SORT_DIRECTION) params["sortDir"] = search.sortDir;
  if (search.allAds) params["allAds"] = "true";
  // task-9 窄开关：默认 false → 不写 URL；只有打开「只显示有花费的」才写出。
  if (search.deliveringOnly) params["deliveringOnly"] = "true";
  // task-3「暂停-建议看」：默认关闭 → 不写 URL；打开才写出（可分享）。
  if (search.pausedWorthWatching) params["pausedWorthWatching"] = "true";
  // task-14「包含已结束」：默认关闭 → 不写 URL；打开才写出（可分享 / 刷新还原）。
  if (search.includeEnded) params["includeEnded"] = "true";
  if (search.history) params["history"] = "true";
  return params;
}

/** 供 TanStack Router 的 `stringifySearch` 使用：输出 `?a=1&b=2`。 */
export function adManagementSearchToQueryString(search: object): string {
  const params = adManagementSearchToParams(
    parseAdManagementSearch(search as Record<string, unknown>),
  );
  const queryString = new URLSearchParams(params).toString();
  return queryString.length > 0 ? `?${queryString}` : "";
}
